import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Switch,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  PlayArrow as TestIcon,
  Assignment as AssignIcon,
} from "@mui/icons-material";
import { FaRobot } from "react-icons/fa";
import { MdGroups, MdCheckCircle, MdPhone } from "react-icons/md";
import { MaterialReactTable } from "material-react-table";
import toast from "react-hot-toast";
import apiClient from "../../services/apiClient";
import { setAssistants } from "../../features/assistants/AssistantsSlice";
import PageLoader from "../Loading/PageLoader";

const AssistantsList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.userInfo?.user?.id);
  // const { assistants } = useSelector((state) => state?.assistants);

  const [assistants, setAssistantsState] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [extensions, setExtensions] = useState([]);
  const [assignedNumber, setAssignedNumber] = useState("");
  const [assignedExtension, setAssignedExtension] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [paginationInfo, setPaginationInfo] = useState({
    total_count: 0,
    total_pages: 0,
  });

  useEffect(() => {
    fetchAssistants();
  }, [pagination.pageIndex, pagination.pageSize]);

  const fetchAssistants = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/assistants", {
        page: pagination.pageIndex + 1,
        page_size: pagination.pageSize,
      });

      if (response?.status && Array.isArray(response?.data)) {
        // Map backend response to table format
        const mapped = response.data.map((item) => ({
          id: item._id,
          name: item.name || "Unnamed",
          description: item.description || "-",
          model: item.llm_config_id || "-",
          voice_provider: item.voice_id || "-",
          language: "English", 
          status: item.status || "deactive",
          updated_at: item.updated_at,
          created_at: item.created_at,
          tools: item.tools || [],
          transcriber_id: item.transcriber_id,
          analysis_id: item.analysis_id,
        }));

        setAssistantsState(mapped);
        dispatch(setAssistants(mapped));
        setPaginationInfo({
          total_count: response.pagination?.total_count || 0,
          total_pages: response.pagination?.total_pages || 0,
        });
      } else {
        toast.error("Invalid API response format");
        setAssistantsState([]);
      }
    } catch (error) {
      toast.error("Failed to fetch assistants");
      console.error(error);
      setAssistantsState([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === "active" ? "deactive" : "active";
      await apiClient.patch(`/assistant/update-status/${id}`, {
        status: newStatus,
      });
      fetchAssistants();
      toast.success("Status updated successfully");
    } catch (error) {
      toast.error("Failed to update status");
      console.error(error);
    }
  };
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this assistant?")) {
      try {
        await apiClient.delete(`/assistants/${id}`);
        fetchAssistants();
        toast.success("Assistant deleted successfully");
      } catch (error) {
        toast.error("Failed to delete assistant");
        console.error(error);
      }
    }
  };

  const handleDuplicate = async (assistant) => {
    try {
      // Fetch full assistant data by ID
      const response = await apiClient.get(`/assistants/${assistant.id}`);
      const fullAssistant = response?.data;

      // Create duplicate with modified name
      const duplicatePayload = {
        name: `${fullAssistant.name} (Copy)`,
        description: fullAssistant.description || "",
        llm_config_id: fullAssistant.llm_config_id,
        transcriber_id: fullAssistant.transcriber_id,
        voice_id: fullAssistant.voice_id,
        analysis_id: fullAssistant.analysis_id,
        tools: fullAssistant.tools || [],
        advanced_settings: fullAssistant.advanced_settings || {},
        status: "draft", // Create duplicate as draft
        user_id: userId,
      };

      await apiClient.post("/assistants", duplicatePayload);
      fetchAssistants();
      toast.success("Assistant duplicated successfully");
    } catch (error) {
      toast.error("Failed to duplicate assistant");
      console.error(error);
    }
  };

  const handleOpenAssignModal = async (assistant) => {
    setSelectedAssistant(assistant);
    setAssignedNumber("");
    setAssignedExtension("");
    setAssignModalOpen(true);

    // Fetch phone numbers and extensions
    try {
      const numbersResponse = await apiClient.get("/assignedPhoneDetails");
      if (numbersResponse?.data) {
        // Map phone details with both number and username
        const phoneDetails = numbersResponse.data
          .filter(item => item?.data?.effective_caller_id_number)
          .map(item => ({
            number: item?.data?.effective_caller_id_number,
            username: item?.data?.username,
            id: item?._id,
          }));
        setPhoneNumbers(phoneDetails);

        // Extract unique extensions
        const uniqueExtensions = [...new Set(numbersResponse.data.map(item => item?.data?.username).filter(Boolean))];
        setExtensions(uniqueExtensions);
      }
    } catch (error) {
      console.error("Failed to fetch numbers and extensions:", error);
      toast.error("Failed to fetch numbers and extensions");
    }
  };

  const handleCloseAssignModal = () => {
    setAssignModalOpen(false);
    setSelectedAssistant(null);
    setAssignedNumber("");
    setAssignedExtension("");
  };

  const handleAssignExtension = async () => {
    if (!assignedNumber || !assignedExtension) {
      toast.error("Please select both number and extension");
      return;
    }

    try {
      await apiClient.put(`/assistants/${selectedAssistant.id}`, {
        assigned_number: assignedNumber,
        assigned_extension: assignedExtension,
      });

      toast.success("Extension assigned successfully");
      handleCloseAssignModal();
      fetchAssistants();
    } catch (error) {
      toast.error("Failed to assign extension");
      console.error(error);
    }
  };

  const columns = [
    {
      accessorKey: "name",
      header: "Assistant Name",
      size: 200,
    },
    {
      accessorKey: "model",
      header: "Model",
      size: 150,
      Cell: ({ cell }) => (
        <Chip label={cell.getValue() || "GPT-4"} size="small" color="primary" />
      ),
    },
    {
      accessorKey: "voice_provider",
      header: "Voice Provider",
      size: 150,
      Cell: ({ cell }) => (
        <Chip label={cell.getValue() || "ElevenLabs"} size="small" />
      ),
    },
    {
      accessorKey: "language",
      header: "Language",
      size: 100,
      Cell: ({ cell }) => cell.getValue() || "English",
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 100,
      Cell: ({ row }) => (
       <Switch
          checked={row.original.status === "active"}
          onChange={() =>
            handleToggleStatus(row.original.id, row.original.status)
          }
        />
      ),
    },
    {
      accessorKey: "updated_at",
      header: "Last Modified",
      size: 150,
      Cell: ({ cell }) =>
        cell.getValue()
          ? new Date(cell.getValue()).toLocaleDateString()
          : "N/A",
    },
    {
      accessorKey: "actions",
      header: "Actions",
      size: 200,
      Cell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => navigate(`/assistants/${row.original.id}/edit`)}
            title="Edit"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          {/* <IconButton
            size="small"
            onClick={() => handleDuplicate(row.original)}
            title="Duplicate"
          >
            <CopyIcon fontSize="small" />
          </IconButton> */}
          <IconButton
            size="small"
            onClick={() => handleOpenAssignModal(row.original)}
            title="Assign Extension"
            color="primary"
          >
            <AssignIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => navigate(`/assistants/${row.original.id}/test`)}
            title="Test"
          >
            <TestIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(row.original.id)}
            title="Delete"
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  // Mock stats - replace with actual data
  const stats = {
    total: assistants.length,
    active: assistants.filter((a) => a.status === "active").length,
    callsToday: 0,
  };

  // if (loading && assistants.length === 0) {
  //   return <PageLoader />;
  // }

  return (

    <>

    {loading && assistants.length === 0 && <PageLoader />}
    
    <div className="pageTitle1 d-flex justify-content-between align-items-center px-5 py-4">
      <div>
        <h3>AI Assistants</h3>
        <p>Manage your AI voice agents and configurations</p>
      </div>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/assistants/create")}
          sx={{
            backgroundColor: "#6859f7ff",
            color: "#FFF",
            fontWeight: 600,
            borderRadius: "8px",
            px: 3, py: 1.5,
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
          }}
        >
          Create Assistant
        </Button>
      </div>

    <Box sx={{ p: 3 }}>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card
            elevation={0}
            sx={{
              borderRadius: "12px",
              background: "#667eea",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "10px",
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <MdGroups style={{ fontSize: 28, color: "white" }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: "white" }}>
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.9)", fontWeight: 500 }}>
                    Total Assistants
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            elevation={0}
            sx={{
              borderRadius: "12px",
              background: "#10b981",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "10px",
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <MdCheckCircle style={{ fontSize: 28, color: "white" }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: "white" }}>
                    {stats.active}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.9)", fontWeight: 500 }}>
                    Active Assistants
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            elevation={0}
            sx={{
              borderRadius: "12px",
              background: "#3b82f6",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "10px",
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <MdPhone style={{ fontSize: 28, color: "white" }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: "white" }}>
                    {stats.callsToday}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.9)", fontWeight: 500 }}>
                    Total Calls Today
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search assistants..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          // size="small"
          sx={{
            background:"#FFF",
            p: 2,
            borderRadius:"10px",
          }}
        />
      </Box>

      {/* Table */}
      <MaterialReactTable
        columns={columns}
        data={assistants.filter((a) =>
          a.name?.toLowerCase().includes(searchText.toLowerCase())
        )}
        manualPagination
        rowCount={paginationInfo.total_count}
        onPaginationChange={setPagination}
        state={{
          isLoading: loading,
          pagination: pagination,
        }}
        enableRowActions={false}
        enableColumnActions={false}
        enableSorting={true}
        enablePagination={true}
        muiTablePaginationProps={{
          showFirstButton: true,
          showLastButton: true,
        }}
      />

      {/* Assign Extension Modal */}
      <Dialog open={assignModalOpen} onClose={handleCloseAssignModal} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Extension to {selectedAssistant?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Phone Number</InputLabel>
              <Select
                value={assignedNumber}
                onChange={(e) => setAssignedNumber(e.target.value)}
                label="Phone Number"
              >
                {phoneNumbers.map((phone) => (
                  <MenuItem key={phone.id} value={phone.number}>
                    {phone.number} (Ext: {phone.username})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Extension</InputLabel>
              <Select
                value={assignedExtension}
                onChange={(e) => setAssignedExtension(e.target.value)}
                label="Extension"
              >
                {extensions.map((ext) => (
                  <MenuItem key={ext} value={ext}>
                    {ext}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAssignModal}>Cancel</Button>
          <Button onClick={handleAssignExtension} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    
    </>

  );
};

export default AssistantsList;
